import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

// 예약된 슬러그 목록
const RESERVED_SLUGS = [
  'qr', 'privacy-policy', 'terms', 'delete-account', 'assets', 'api',
  'login', 'register', 'dashboard', 'home', 'admin', 'system', 'app',
  'www', 'mail', 'ftp', 'smtp', 'pop', 'imap', 'webmail', 'cpanel',
  'phpmyadmin', 'wp-admin', 'wp-content', 'wp-includes', 'wordpress',
  'blog', 'forum', 'shop', 'store', 'cart', 'checkout', 'payment',
  'support', 'help', 'faq', 'contact', 'about', 'team', 'careers',
  'privacy', 'terms-of-service', 'refund-policy', 'legal', 'sitemap', 'robots',
  'favicon', 'manifest', 'service-worker', 'sw', 'pwa',
  // 🆕 구독 관리 관련 예약 슬러그
  'subscription-management', 'paywall', 'payment-success', 'invoices',
  'cards', 'stats', 'domain-settings', 'custom-domain-settings',
  'business-profile', 'firebase-test', 'forgot-password'
];

const UserProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const [profileStatus, setProfileStatus] = useState<'loading' | 'found' | 'not-found'>('loading');

  // 예약된 슬러그인지 확인
  const isReservedSlug = username && RESERVED_SLUGS.includes(username.toLowerCase());

  // 디버깅 정보
  console.log('🔍 [UserProfilePage] Component rendered');
  console.log('🔍 [UserProfilePage] Username:', username);
  console.log('🔍 [UserProfilePage] Is reserved slug:', isReservedSlug);
  console.log('🔍 [UserProfilePage] Current URL:', window.location.href);
  console.log('🔍 [UserProfilePage] Component name:', 'UserProfilePage');
  console.log('🔍 [UserProfilePage] Profile status:', profileStatus);

  // 예약된 슬러그 즉시 리다이렉트 처리
  useEffect(() => {
    if (isReservedSlug) {
      console.log('🚨 [UserProfilePage] Reserved slug detected, redirecting...');
      console.log('🚨 [UserProfilePage] Username:', username);
      console.log('🚨 [UserProfilePage] Current URL:', window.location.href);
      
      // 이제 /u/:username 라우트이므로 예약된 슬러그 체크는 불필요
      // 하지만 혹시 모를 경우를 대비해 홈으로 리다이렉트
      console.log('🚨 [UserProfilePage] Reserved slug detected in /u/ route, redirecting to home');
      window.location.replace('/');
    }
  }, [isReservedSlug, username]);

  // 추가 보안: subscription-management가 /u/ 라우트에 접근하는 경우 차단
  useEffect(() => {
    if (username === 'subscription-management') {
      console.log('🚨 [UserProfilePage] subscription-management detected in /u/ route, redirecting...');
      window.location.replace('/subscription-management');
    }
  }, [username]);

  // 실제 프로필 로딩 (Firestore에서 조회)
  useEffect(() => {
    if (!username || isReservedSlug) return;

    const loadProfile = async () => {
      try {
        // Firestore에서 프로필 조회
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase/config');
        
        // customLinks 컬렉션에서 매핑 찾기
        const linkRef = doc(db, 'customLinks', username);
        const linkDoc = await getDoc(linkRef);
        
        if (linkDoc.exists()) {
          const linkData = linkDoc.data();
          console.log('✅ [UserProfilePage] Profile found:', linkData);
          setProfileStatus('found');
        } else {
          console.log('❌ [UserProfilePage] Profile not found for username:', username);
          setProfileStatus('not-found');
        }
      } catch (error) {
        console.error('❌ [UserProfilePage] Error loading profile:', error);
        setProfileStatus('not-found');
      }
    };

    loadProfile();
  }, [username, isReservedSlug]);

  if (isReservedSlug) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">예약된 슬러그</h1>
          <p className="text-gray-600 mb-4">
            <strong>{username}</strong>은(는) 예약된 슬러그입니다.
          </p>
          <p className="text-sm text-gray-500">
            다른 사용자명을 사용해주세요.
          </p>
          <div className="mt-4">
            <a 
              href="/" 
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              홈으로 돌아가기
            </a>
          </div>
        </div>
      </div>
    );
  }

  // 프로필 상태에 따른 렌더링
  if (profileStatus === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Profile Loading</h1>
          <p className="text-gray-600 mb-4">
            Loading profile for: <strong>{username}</strong>
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-500 mt-4">
            If this page doesn't redirect automatically, please refresh the page.
          </p>
        </div>
      </div>
    );
  }

  if (profileStatus === 'not-found') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Profile not found</h1>
          <p className="text-gray-600 mb-4">
            The profile <strong>{username}</strong> could not be found.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            The user may not exist or the profile may be private.
          </p>
          <div className="mt-4">
            <a 
              href="/" 
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              홈으로 돌아가기
            </a>
          </div>
        </div>
      </div>
    );
  }

  // 프로필이 발견된 경우 - 실제 프로필 표시
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
        <h1 className="text-2xl font-bold text-green-600 mb-4">Profile Found</h1>
        <p className="text-gray-600 mb-4">
          Profile for: <strong>{username}</strong>
        </p>
        <p className="text-sm text-gray-500 mb-4">
          프로필 페이지가 성공적으로 로드되었습니다.
        </p>
        <div className="mt-4">
          <a 
            href="/" 
            className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            홈으로 돌아가기
          </a>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
